import { useState , useEffect } from "react";
import { useNavigate } from "react-router-dom";
// ** MUI Components
import {
  Avatar,
  Box,
  Typography,
  Tooltip,
  AvatarGroup,
} from "@mui/material";
import CustomChip from "../../../components/mui/chip";
import { GetServiceRequests } from "./GetServiceRequest";
import { SaveServiceRequest } from "./SaveServiceRequest";
import { useServiceRequestPermission } from "../permissions/RequestPermissions";

// ** Icon Imports
import Icon from "../../../components/icon";

export const useIndexLogic = () => {
  //#region Permissions
  const { ServiceRequestPermissionProps } = useServiceRequestPermission();
  const { hasGetJobRequestGetPermission } = ServiceRequestPermissionProps;
  //#endregion Permissions

  //#region Hooks
  const navigate = useNavigate();
  //#endregion
 
  //#region Constants
  const contentHeightFixed = false;
  //#endregion End of Constants

  //#region States

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [paginationText, setPaginationText] = useState('');
  const [openNewRequestDlg, setOpenNewRequestDlg] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [openTagManagementDlg, setOpenTagManagementDlg] = useState(false);

  const tagManagementData = { title: 'Tag Management' }

  //#endregion end of states

  //#region Handlers and methods

  useEffect(() => {
    if(hasGetJobRequestGetPermission){
      fetchRequests(0, 0);
    }
  }, [hasGetJobRequestGetPermission]);

  useEffect(() => {
      if(!hasGetJobRequestGetPermission) return;
      setFilteredRows( () => 
         data.filter(row =>    
         Object.values(row).some(value =>
             value && value.toString().toLowerCase().includes(searchText.toLowerCase())
         ))
       )
  }, [searchText])

  useEffect(() => {
    const total = filteredRows.length;
    let start = paginationModel.page * paginationModel.pageSize + 1;
    let end = Math.min((paginationModel.page + 1) * paginationModel.pageSize, total);
  
    if (total === 0) {
      setPaginationText("0-0 of 0");
      return;
    }

    if (paginationModel.pageSize === -1) {
      start = 1;
      end = total;
    }
  
    setPaginationText(`${end} of ${total}`);
  }, [filteredRows, paginationModel.page, paginationModel.pageSize]);

  const customerID = 0
  const requestID = 0

const fetchRequests = async (customerID, requestID) => {
  if (!hasGetJobRequestGetPermission) return;

  try {
    setLoading(true);
    let requests = await GetServiceRequests(customerID, requestID);

    // Filter to keep only entityType 'request'
    const filteredRequests = requests.filter(
      (x) => x.entityType.toLowerCase() === "request"
    );

    // Sort descending by 'requestedOn' first, then createDate, then requestID
    const sortedRequests = filteredRequests.sort((a, b) => {
      const dateDiff =
        new Date(b.requestedOn) - new Date(a.requestedOn);

      if (dateDiff !== 0) {
        return dateDiff; // primary: requestedOn
      }

      // fallback: createDate has actual time info
      if (a.createDate && b.createDate) {
        return new Date(b.createDate) - new Date(a.createDate);
      }

      // final fallback: requestID
      return (b.requestID || 0) - (a.requestID || 0);
    });

    setData(sortedRequests);
    setFilteredRows(sortedRequests);
  } catch (error) {
    console.error("Error fetching Requests data:", error);
  } finally {
    setLoading(false);
  }
};


  // const fetchData = async () => {
  //   try {
  //     let cus = await GetCustomer(0, navigate);
  //     const filteredData = filterData(cus, keysToKeep);
  //     setData(filteredData);
  //     setAllCustomers(cus);
  //     //console.log(cus);
  //   } catch (error) {
  //     console.error("Error fetching Customer data:", error);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const handleOpenNewRequestDlg = () => {
    setOpenNewRequestDlg(true);
  }
  const handleCloseNewRequestDlg = () => {
    fetchRequests(0,0);
    setOpenNewRequestDlg(false);    
  }

  const handleCloseTagManagementDlg = () => setOpenTagManagementDlg(false);

  const initialServiceRequestState = {
    requestID: 0,
    requestTitle: null,
    reqDescription: null,
    completeWorkBy: null,
    customerID: 0,
    jobSiteID: 0,
    jobsiteContactID: 0,
    requestSource: null,
    status: null,
    availabilityTime: [],
    availabilityDays: [],
    tags: [],
    assignedToUsers: []
  }
  const [serviceRequest, setServiceRequest] = useState(initialServiceRequestState);
  const [Tag, setTag]  = useState('');

  const handleOpenTagManagementDlg = (tags, id, requestTitle, status) => {
    const formattedTags = tags.map(tag => ({ tagName: tag }));
    setServiceRequest((prev) => {
      return { ...prev, requestID: id, requestTitle: requestTitle, tags: formattedTags, status: status };
    })
    setOpenTagManagementDlg(true);
  }

  const handleAddTag = async (tagName) => {
    if (tagName.trim() === '') return;
  
    const lowerCaseTagName = tagName.toLowerCase();
  
    const tagExists = serviceRequest.tags?.some(
      (tag) => tag.tagName.toLowerCase() === lowerCaseTagName
    );
  
    if (tagExists) return;
  
    const updatedTags = [...(serviceRequest.tags || []), { tagName }];
    const updatedRequest = {
      requestID: serviceRequest.requestID,
      requestTitle: serviceRequest.requestTitle,
      status: serviceRequest.status,
      tags: updatedTags,
    };
  
    setServiceRequest(updatedRequest);
    setTag('');
  
    try {
      await SaveServiceRequest({ requestID: serviceRequest.requestID, requestTitle: serviceRequest.requestTitle, tags: updatedTags, status: serviceRequest.status });
      await fetchRequests(0,0);
    } catch (error) {
      console.error('Error saving tag:', error);
    }
  };

  const handleDeleteTag = async (tagName) => {
    const updatedTags = serviceRequest.tags.filter(tag => tag.tagName !== tagName);
  
    const updatedRequest = {
      requestID: serviceRequest.requestID,
      requestTitle: serviceRequest.requestTitle,
      status: serviceRequest.status,
      tags: updatedTags,
    };
  
    setServiceRequest(updatedRequest);
  
    try {
      await SaveServiceRequest({
        requestID: serviceRequest.requestID,
        requestTitle: serviceRequest.requestTitle,
        status: serviceRequest.status,
        tags: updatedTags,
      });
  
      await fetchRequests(0,0);
    } catch (error) {
      console.error('Error deleting tag:', error);
    }
  };

  const handleDeleteTagByID = async (tagName, tags, id, requestTitle, status) => {
    const updatedTags = tags.filter(tag => tag !== tagName);
    const formattedTags = updatedTags.map(tag => ({ tagName: tag }));
  
    const updatedRequest = {
      requestID: id,
      requestTitle: requestTitle,
      status: status,
      tags: formattedTags,
    };
  
    setServiceRequest(updatedRequest);
  
    try {
      await SaveServiceRequest({
        requestID: id,
        requestTitle: requestTitle,
        status: status,
        tags: formattedTags,
      });
  
      await fetchRequests(0,0);
      setServiceRequest({});
    } catch (error) {
      console.error('Error deleting tag by ID:', error);
    }
  };

  const getInitials = (name) => {
    if (!name || typeof name !== 'string') return '';
    
    return name
      .split(' ')
      .filter(part => part.trim().length > 0)
      .map(part => part[0].toUpperCase())
      .join('');
  };

  const columns = [
    {
      // flex: 0.1,
      field: "id",
      hide: true,
      minWidth: 110,
      headerName: "ID",
      renderCell: ({ row }) => {
        const { id, requestID } = row;
  
        return (
          <Box 
            sx={{ 
              display: "flex", 
              alignItems: "center", 
              height: "100%",
              cursor:'pointer', 
              '&:hover' : {
                textDecoration:'underline'
              },
            }} 
            onClick={() => navigate(`/requests/detail/${requestID}`)}
          >
            <Typography sx={{ flexGrow: 1 }}>{id}</Typography>
          </Box>
        );
      },
    },
    {
      // flex: 0.1,
      field: "requestTitle",
      headerName: "Request Title",
      // width:180,
      minWidth: 180,
      renderCell: ({ row }) => {
        const { requestTitle, requestID } = row;
         //console.log(row);customerID

        return (
          <Box 
            sx={{ 
              display: "flex", 
              alignItems: "center", 
              height: "100%",
              cursor:'pointer', 
              '&:hover' : {
                textDecoration:'underline'
              },
            }} 
            onClick={() => navigate(`/requests/detail/${requestID}`)}
          >
            <Typography sx={{ flexGrow: 1 }}>{requestTitle}</Typography>
          </Box>
        );
      },
    },
    {
      // flex: 0.1,
      field: "customerName",
      minWidth: 180,
      headerName: "Customer Name",
      renderCell: ({ row }) => {
        const { customerName , customerID } = row;
  
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" ,
             cursor:'pointer', 
              '&:hover' : {
                textDecoration:'underline'
              },
           }} 
           onClick={() => navigate(`/customers/detail/${customerID}`)}
          >

            <Typography sx={{ flexGrow: 1 }}>{customerName}</Typography>
          </Box>
        );
      },
    },
    {
      // flex: 0.1,
      field: "jobsite",
      headerName: "Jobsite",
      // width: 150,
      minWidth: 150,
      renderCell: (params) => {
        const { row } = params;
  
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              height: "100%", // Take the full height of the cell
            }}
          >
            <Typography
              noWrap
              variant="body2"
              sx={{ color: "text.primary", fontWeight: 600 }}
            >
              {row.jobsiteName}
            </Typography>
            <Typography noWrap color="text.secondary" fontSize="small">
              {row.jobsiteAddress}
            </Typography>
          </Box>
        );
      },
    },
    {
      // flex: 0.1,
      field: "completeWorkBy",
      headerName: "Complete Work By",
      // width: 190,
      minWidth: 190,
      renderCell: ({ row }) => {
        const { completeWorkBy } = row;
  
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography sx={{ flexGrow: 1 }}>{completeWorkBy}</Typography>
          </Box>
        );
      },
    },
    {
      // flex: 0.1,
      field: "createBy",
      headerName: "Created By",
      // width: 140,
      minWidth: 140,
      renderCell: ({ row }) => {
        const { createBy } = row;
  
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography sx={{ flexGrow: 1 }}>{createBy}</Typography>
          </Box>
        );
      },
    },
    {
      // flex: 0.1,
      field: "requestedOn",
      headerName: "Requested On",
      minWidth: 150,
      renderCell: ({ row }) => {
        const { requestedOn } = row;
  
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography sx={{ flexGrow: 1 }}>{requestedOn}</Typography>
          </Box>
        );
      },
    },
    {
      // flex: 0.1,
      field: "source",
      headerName: "Source",
      minWidth: 100,
      renderCell: ({ row }) => {
        const { source } = row;
        return (
          <CustomChip
            rounded
            size="small"
            skin="light"
            sx={{ lineHeight: 1 }}
            color={source === "External" ? "warning" : "info"} //'success' // Use color names: primary, secondary, error, warning, info, success
            label={source}
            backgroundColor="dark"
          />
        );
      },
    },
    {
      // flex: 0.1,
      field: "assignedTo",
      headerName: "Assigned To",
      minWidth: 120,
      renderCell: ({ row }) => {
        const { assignedTo } = row;
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            {assignedTo && assignedTo.length > 0 ? (
              <AvatarGroup
                className="pull-up"
                max={4}
                sx={{
                  "& .MuiAvatar-root": { width: 25, height: 25 },
                  "& .MuiAvatarGroup-avatar": { width: 25, height: 25 },
                }}
              >
                {assignedTo.map((assignee) => (
                  <Tooltip title={assignee.userName}>
                    <Avatar
                      src="/images/avatars/4.png"
                      alt="employee-picture"
                      sx={{ width: 25, height: 25, fontSize: '12px' }}
                    >{getInitials(assignee.userName)}</Avatar>
                  </Tooltip>
                ))}
              </AvatarGroup>
            ) : (
              <Typography variant='body2' color='text.secondary'>Unassigned</Typography>
            )}
          </Box>
        );
      },
    },
    {
      // flex: 0.1,
      field: "status",
      headerName: "Status",
      minWidth: 140,
      renderCell: ({ row }) => {
        const { status } = row;
  
        return (
          <CustomChip
            rounded
            size="small"
            skin="light"
            sx={{ lineHeight: 1 }}
            color={status === "Under Review" ? "info" : status === "Submitted" ? "success" : status === "On Hold" ? "warning" : status === "Canceled" ? "error" : status === "Canceled by Customer" ? "error" :  "secondary"}
            label={status}
          />
        );
      },
    },
    {
      flex: 0.1,
      field: "tags",
      minWidth: 280,
      headerName: "Tags",
      renderCell: ({ row }) => {
        const { tags, requestID, requestTitle, status } = row;

        return (
          <Box
            sx={{
              display: "flex",
              gap: 1,
              flexWrap: "nowrap",
              alignItems: "center",
              height: "100%",
            }}
          >
            <CustomChip
              rounded
              label="Add"
              size="small"
              color="info"
              skin="light"
              sx={{ lineHeight: 1 }}
              onDelete={() => handleOpenTagManagementDlg(tags, requestID, requestTitle, status)}
              deleteIcon={<Icon icon="tabler:circle-plus" />}
            />

            {tags && tags.length > 0 ? (
              tags.length <= 2 ? (
                tags.map((tag, index) => (
                  <>
                    <CustomChip
                      key={index}
                      rounded
                      label={tag}
                      size="small"
                      color="info"
                      skin="light"
                      sx={{ lineHeight: 1 }}
                      onDelete={() => handleDeleteTagByID(tag, tags, requestID, requestTitle, status)}
                    />
                  </>
                ))
              ) : (
                <>
                  <CustomChip
                    rounded
                    label={tags[0]}
                    size="small"
                    color="info"
                    skin="light"
                    sx={{ lineHeight: 1 }}
                    onDelete={() => handleDeleteTagByID(tags[0], tags, requestID, requestTitle, status)}
                  />
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ display: "inline-flex", alignItems: "center" }}
                  >
                    ...
                  </Typography>
                  <CustomChip
                    rounded
                    label="See More"
                    size="small"
                    color="info"
                    skin="light"
                    sx={{ 
                      lineHeight: 1,
                      '&:hover': {
                        backgroundColor: 'rgba(0, 58, 112, 0.16)',
                      },  
                    }}
                    onClick={() => handleOpenTagManagementDlg(tags, requestID, requestTitle, status)}
                  />
                </>
              )
            ) : (
              <Typography variant="body2" color="textSecondary">
                No tags
              </Typography>
            )}
          </Box>
        );
      },
    },
  ];

  const handleClearSelection = () => {
    setRowSelectionModel([]);
    setSelectedRows(0);
  };

  const handleGridSelection = (selectedRows) => setSelectedRows(selectedRows);

  const handleSearch = (val) => setSearchText(val);

  const handleClearSearch = () => setSearchText('');
  
    //#endregion end of handlers and methods
    

  const IndexProps = { 
    columns,     
    contentHeightFixed,
    handleOpenNewRequestDlg,
    handleCloseNewRequestDlg,
    handleOpenTagManagementDlg,
    handleCloseTagManagementDlg,
    handleClearSelection,
    handleGridSelection,
    handleSearch,
    handleClearSearch,
    loading,
    setLoading,
    filteredRows, 
    data,
    paginationText,
    searchText, 
    paginationModel, 
    setPaginationModel,
    openNewRequestDlg, 
    selectedRows, 
    rowSelectionModel, 
    setRowSelectionModel,
    openTagManagementDlg, 
    tagManagementData,
    fetchRequests,
    handleAddTag,
    handleDeleteTag,
    Tag,
    setTag,
    serviceRequest
  }

  return {    
    IndexProps
  };
}
